import '@orus.eu/pharaoh-next/styles/theme.scoped.css'
import { memo, type ReactNode, useState } from 'react'

import { usePharaohTheme } from '@orus.eu/pharaoh'
import { PortalContainerContext } from '@orus.eu/pharaoh-next/lib/portal-container-context'

/**
 * The pharaoh-next rendering surface. `'fo'` (client portal, e.g. /account) honors partner
 * co-branding fonts; `'bo'` (backoffice) keeps the Inter typography surface. See
 * `theme.scoped.css` (`[data-pharaoh-surface]`).
 */
type PharaohNextSurface = 'fo' | 'bo'

/**
 * Confines pharaoh-next (Tailwind v4) styling to its subtree, alongside the Emotion/MUI app.
 * Mirrors the active pharaoh v1 theme onto `data-theme` so the design-tokens `[data-theme]`
 * variables (including partner co-branded skins) resolve in scope; falls back to `light` when
 * no theme is set. Wrap ONLY migrated pharaoh-next content — do not wrap v1 Emotion content,
 * to keep the scoped base rules off v1 elements.
 *
 * `surface="fo"` (default, client portal) provides the responsive FO typescale to content and
 * portal content, and honors partner co-branding fonts; `'bo'` (backoffice) stays on Inter.
 *
 * The host element is exposed via `PortalContainerContext` so Base UI portals (Dialog,
 * Popover…) can render inside the themed scope — see `PortalContainerContext` from
 * `@orus.eu/pharaoh-next/lib/portal-container-context`.
 */
export const PharaohNextScope = memo(function PharaohNextScope({
  children,
  surface = 'fo',
}: {
  children: ReactNode
  surface?: PharaohNextSurface
}) {
  const [container, setContainer] = useState<HTMLElement | null>(null)
  const { theme } = usePharaohTheme()
  return (
    <div
      data-pharaoh-next=""
      data-pharaoh-surface={surface}
      data-theme={theme ?? 'light'}
      data-typescale={surface === 'fo' ? 'fo' : undefined}
      ref={setContainer}
    >
      <PortalContainerContext value={container}>{children}</PortalContainerContext>
    </div>
  )
})
